import { useState, useCallback } from 'react'
import useNavigateTo from 'hooks/useNavigateTo'

export const CHAT_TRIP_PLANNER_ROUTE = '/trip-planner/chat-trip-planner' as const
export const NEW_CHAT_ROUTE_SEGMENT = 'new' as const

export type ChatNavigationOptions = {
  shouldUpdateUrl?: boolean
}

interface UseChatNavigationArgs {
  routeChatId: string | undefined
}

export function useChatNavigation({
  routeChatId,
}: UseChatNavigationArgs) {
  const [chatId, setChatIdState] = useState<number | undefined>(undefined)
  const navigateTo = useNavigateTo()

  const isNewChatRoute = routeChatId === NEW_CHAT_ROUTE_SEGMENT
  const normalizedRouteChatId = !isNewChatRoute && routeChatId ? Number(routeChatId) : undefined
  const hasRouteChatId = Number.isFinite(normalizedRouteChatId)
  const isLandingRoute = !routeChatId
  const existingRouteChatId = hasRouteChatId ? normalizedRouteChatId : undefined

  const navigateToChatRoute = useCallback((nextChatId?: number) => {
    const nextPath = typeof nextChatId === 'number'
      ? `${CHAT_TRIP_PLANNER_ROUTE}/${nextChatId}`
      : CHAT_TRIP_PLANNER_ROUTE
    navigateTo(nextPath)
  }, [navigateTo])

  const navigateToNewChatRoute = useCallback(() => {
    navigateTo(`${CHAT_TRIP_PLANNER_ROUTE}/${NEW_CHAT_ROUTE_SEGMENT}`)
  }, [navigateTo])

  const syncChatId = useCallback((nextChatId: number | undefined, options?: ChatNavigationOptions) => {
    setChatIdState(nextChatId)
    if (options?.shouldUpdateUrl === false) {
      return
    }
    navigateToChatRoute(nextChatId)
  }, [navigateToChatRoute])

  const setChatIdForStreaming = useCallback((nextChatId: number | undefined) => {
    syncChatId(nextChatId)
  }, [syncChatId])

  return {
    chatId,
    setChatId: syncChatId,
    setChatIdForStreaming,
    isNewChatRoute,
    isLandingRoute,
    hasRouteChatId,
    existingRouteChatId,
    navigateToNewChatRoute,
  }
}
